import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Checkbox,
  Page,
  Form,
  Card,
  InlineGrid,
  Text,
  InlineStack,
  Select,
  Bleed,
  Box,
  BlockStack,
  FormLayout,
  Button,
  TextField,
  DropZone,
  ResourceList,
  ResourceItem,
  LegacyStack,
  InlineError,
  RadioButton
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { AlertCircle, CheckCircle } from "lucide-react";
import axios from "axios";
import { API_URL } from "constants";
import "./customer-form.scss";
import Loading from "components/loading/Loading";
import { APP_CONFIG } from "../../config/app-config";
import { uploadWithFormKey } from "services/upload-file.service";
import { postQuestionnaireValidationAttempt } from "services/customers.service";
import { validateEmail, validatePhone } from "common/validator";
import { getAllCountryOptions, countryCodes, getAllCountryISOptions } from "constants/countries";
import { splitVatCode, validateVatCode } from "utils/helperFunctions";
import useIsPhone from "features/files/use-is-phone";

function separatePhoneNumber(fullPhone, countryCode) {
  if (!fullPhone || !countryCode)
    return { countryCode: countryCode || "", phoneNumber: fullPhone || "" };

  const cleanPhone = fullPhone.replace(/\D/g, "");
  const cleanCountryCode = countryCode.replace(/\D/g, "");

  if (cleanPhone.startsWith(cleanCountryCode)) {
    return {
      countryCode: countryCode,
      phoneNumber: cleanPhone.substring(cleanCountryCode.length),
    };
  }

  return { countryCode: countryCode, phoneNumber: cleanPhone };
}

/**
 * The currencies partners actually name. The field itself stays free text —
 * these are one click instead of typing (Desk #101: EUR and USD with the same
 * partner is the common case).
 */
const POPULAR_CURRENCIES = ["EUR", "USD", "GBP", "PLN"];

const EMPTY_ACCOUNT = {
  iban: "",
  iban2: "",
  bankName: "",
  swiftCode: "",
  currency: "",
  comment: "",
};

/** Account fields are typed with spaces and lower case; the DB gets neither. */
function cleanAccountNumber(value) {
  return (value || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
}

function cleanCurrency(value) {
  return (value || "").toUpperCase().replace(/[^A-Z]/g, "").slice(0, 3);
}

/** Map GET /customers/form-key/:formKey JSON to form state */
function mapCustomerEntityToFormData(customerEntity) {
  const country = customerEntity.country || "LT";
  const countryCode = countryCodes[country] || "";

  let phoneNumber = customerEntity.phone || "";
  if (phoneNumber && countryCode) {
    const separated = separatePhoneNumber(phoneNumber, countryCode);
    phoneNumber = separated.phoneNumber;
  }

  const vatParts = customerEntity.vatCode
    ? (() => {
        const vatCodeSplit = splitVatCode(customerEntity.vatCode);
        return {
          vatCodeLetters:
            vatCodeSplit.letters ||
            (customerEntity.country ? customerEntity.country.toUpperCase() : ""),
          vatCodeFigures: vatCodeSplit.figures || "",
        };
      })()
    : {
        vatCodeLetters: customerEntity.country
          ? customerEntity.country.toUpperCase()
          : "",
        vatCodeFigures: "",
      };

  const uploadedFromApi = customerEntity.uploadedFiles;

  // The customer may hold several accounts. The first one stays in the flat
  // fields the form has always used (iban / iban2 / bankName / swiftCode), so
  // nothing below this line had to learn about lists; the rest go to
  // `extraAccounts`.
  const accountsFromApi = Array.isArray(customerEntity.accounts)
    ? customerEntity.accounts
    : [];
  const [firstAccount, ...restAccounts] = accountsFromApi;
  const primaryFromApi = accountsFromApi.findIndex(
    (account) => account.isPrimary
  );

  return {
    ...customerEntity,
    uploadedFiles: Array.isArray(uploadedFromApi) ? uploadedFromApi : [],
    iban: (firstAccount ? firstAccount.iban : customerEntity.iban) || "",
    iban2: (firstAccount ? firstAccount.iban2 : customerEntity.iban2) || "",
    bankName:
      (firstAccount ? firstAccount.bankName : customerEntity.bankName) || "",
    swiftCode:
      (firstAccount ? firstAccount.swiftCode : customerEntity.swiftCode) || "",
    currency: (firstAccount && firstAccount.currency) || "",
    comment: (firstAccount && firstAccount.comment) || "",
    extraAccounts: restAccounts.map((account) => ({
      ...EMPTY_ACCOUNT,
      iban: account.iban || "",
      iban2: account.iban2 || "",
      bankName: account.bankName || "",
      swiftCode: account.swiftCode || "",
      currency: account.currency || "",
      comment: account.comment || "",
    })),
    primaryAccountIndex: primaryFromApi > 0 ? primaryFromApi : 0,
    country: customerEntity.country || country,
    countryCode: countryCode,
    phone: phoneNumber,
    street: customerEntity.street || "",
    city: customerEntity.city || "",
    region: customerEntity.region || "",
    houseNumber: customerEntity.houseNumber || "",
    zipCodeLetters: customerEntity.zipCodeLetters || "",
    zipCodeFigures: customerEntity.zipCodeFigures || "",
    ...vatParts,
    legal_name: customerEntity.legalName || "",
    isVatPayer: customerEntity.isVatPayer,
  };
}

/** The page that replaces the form once it is sent, or when the link is no good. */
function FormOutcome({ Icon, iconClass, heading, text }) {
  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-3 px-4 py-16 text-center">
      <Icon className={`h-14 w-14 ${iconClass}`} aria-hidden="true" />
      <h1 className="text-xl font-semibold text-gray-900">{heading}</h1>
      <p className="text-gray-600">{text}</p>
    </div>
  );
}

function CustomerForm() {
  const { formKey } = useParams();
  const isPhone = useIsPhone();
  // The micro size is too small to hit with a finger.
  const currencyButtonSize = isPhone ? "medium" : "micro";
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showFormError, setShowFormError] = useState(false);
  const [openFileDialog, setOpenFileDialog] = useState(false);
  const [isFileUploadLoading, setIsFileUploadLoading] = useState(false);
  const [formSubmitLoading, setFormSubmitLoading] = useState(false);
  const [formData, setFormData] = useState({
    companyName: "",
    name: "",
    legal_name: "",
    street: "",
    city: "",
    region: "",
    country: "",
    countryCode: "",
    zipCodeLetters: "",
    zipCodeFigures: "",
    houseNumber: "",
    officeNumber: "",
    iban: "",
    iban2: "",
    vatCodeLetters: "",
    vatCodeFigures: "",
    phone: "",
    email: "",
    website: "",
    bankName: "",
    swiftCode: "",
    currency: "",
    comment: "",
    extraAccounts: [],
    primaryAccountIndex: 0,
    companyCode: "",
    contactFullName: "",
    contactPhone: "",
    contactPosition: "",
    contactEmail: "",
    signatureFullName: "",
    signaturePosition: "",
    signatureEmail: "",
    signatureBasisOfRepresentation: "",
    invoiceEmail: "",
    invoiceEmail2: "",
    paymentsFullName: "",
    paymentsPhone: "",
    paymentsPosition: "",
    paymentsEmail: "",
    signerPosition: "",
    signerFullName: "",
    signerFirstName: "",
    signerLastName: "",
    officeAddress: "",
    uploadedFiles: [],
    isFileUploadOptional: false,
    isVatPayer: null,
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [isAgreeDeliverElectronically, setIsAgreeDeliverElectronically] =
    useState(false);
  const [isDisagreeDeliverElectronically, setIsDisagreeDeliverElectronically] =
    useState(false);

  const { t } = useTranslation();

  // const handleChange = useCallback(
  //   (field) => (value) => {
  //     setFormData({ ...formData, [field]: value });
  //   },
  //   [formData]
  // );
  const validateIBAN = (value) => {
    // Basic IBAN validation - just check for alphanumeric characters
    const ibanRegex = /^[A-Z0-9]+$/;

    if (!ibanRegex.test(value)) {
      return "IBAN tik raidės ir skaičiai / IBAN must be alphanumeric";
    }
    return null;
  };

  const handleChange = useCallback(
    (field) => (value) => {
      // Only for iban field
      if (field === "iban" || field === "iban2") {
        // Remove invalid chars: allow only A-Z and 0-9
        let cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, "");

        value = cleaned;
      }

      // For VAT code letters: only uppercase letters, max 2 characters
      if (field === "vatCodeLetters") {
        value = value
          .toUpperCase()
          .replace(/[^A-Z]/g, "")
          .substring(0, 2);
      }

      // Currency is free text (a partner may name anything), but it is stored as
      // a 3-letter code, so the field enforces the shape while typing.
      if (field === "currency") {
        value = cleanCurrency(value);
      }

      // For VAT code figures: alphanumeric (numbers and uppercase letters)
      if (field === "vatCodeFigures") {
        value = value.toUpperCase().replace(/[^0-9A-Z]/g, "");
      }

      setFormData((prev) => {
        const newData = {
          ...prev,
          [field]: value,
        };

        // Auto-update country code when country changes
        if (field === "country") {
          const newCountryCode = countryCodes[value] || "";
          newData.countryCode = newCountryCode;

          // If there's an existing phone number, separate it from the country code
          if (prev.phone) {
            const separated = separatePhoneNumber(prev.phone, newCountryCode);
            newData.phone = separated.phoneNumber;
          }

          // Auto-populate VAT code letters with country code if empty
          if (!prev.vatCodeLetters && value) {
            newData.vatCodeLetters = value.toUpperCase();
          }
        } else if (field === "countryCode") {
          // Normalize selected dial code to "+<digits>"
          const dialDigits = (value + "").replace(/\D/g, "");
          const normalizedDial = dialDigits ? `+${dialDigits}` : "";
          newData.countryCode = normalizedDial;

          // Update country based on selected dial code if mismatched
          const isoMatches = Object.entries(countryCodes)
            .filter(([, dial]) => dial === normalizedDial)
            .map(([iso]) => iso);
          if (isoMatches.length > 0 && !isoMatches.includes(prev.country)) {
            newData.country = isoMatches[0];
          }

          // Re-separate phone if present
          if (prev.phone) {
            const separated = separatePhoneNumber(prev.phone, normalizedDial);
            newData.phone = separated.phoneNumber;
          }
        } else if (field === "isVatPayer" && value === false) {
          newData.vatCodeLetters = "";
          newData.vatCodeFigures = "";
        }

        return newData;
      });

      if (field === "iban" || field === "iban2") {
        const error = validateIBAN(value);
        setValidationErrors((prev) => {
          const newErrors = { ...prev, [field]: error };
          // The "IBAN or the alternative number is required" message is written
          // to both fields at once; filling either one satisfies it.
          const twin = field === "iban" ? "iban2" : "iban";
          if (
            value &&
            newErrors[twin] === t("customer_form-iban_or_iban2_required")
          ) {
            delete newErrors[twin];
          }
          return newErrors;
        });
      }

      // Clear validation error for optional invoiceEmail2 when field is cleared or contains only whitespace
      if (field === "invoiceEmail2") {
        const trimmedValue = value ? value.trim() : "";
        if (trimmedValue === "") {
          // Clear error if field is empty or contains only whitespace (field is optional)
          setValidationErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.invoiceEmail2;
            return newErrors;
          });
        } else if (validateEmail(trimmedValue)) {
          // Clear error if email is valid
          setValidationErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.invoiceEmail2;
            return newErrors;
          });
        }
      }
    },
    [t] // countryCodes lives outside the component; `t` is read for the paired IBAN error
  );

  /** Edit one field of the 2nd, 3rd… account. */
  const handleExtraAccountChange = useCallback(
    (index, field) => (rawValue) => {
      let value = rawValue;
      if (field === "iban" || field === "iban2" || field === "swiftCode") {
        value = cleanAccountNumber(value);
      }
      if (field === "currency") {
        value = cleanCurrency(value);
      }

      setFormData((prev) => {
        const extraAccounts = [...prev.extraAccounts];
        extraAccounts[index] = { ...extraAccounts[index], [field]: value };
        return { ...prev, extraAccounts };
      });

      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`extraAccount_${index}_${field}`];
        // "IBAN or the alternative number is required" is written to BOTH number
        // fields, so filling either one satisfies it — clear the twin too, or it
        // stays red under a field the client is meant to leave empty.
        if (value && (field === "iban" || field === "iban2")) {
          const twin = `extraAccount_${index}_${
            field === "iban" ? "iban2" : "iban"
          }`;
          if (newErrors[twin] === t("customer_form-iban_or_iban2_required")) {
            delete newErrors[twin];
          }
        }
        return newErrors;
      });
    },
    [t]
  );

  const handleAddAccount = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      extraAccounts: [...prev.extraAccounts, { ...EMPTY_ACCOUNT }],
    }));
  }, []);

  /**
   * Removing an account renumbers the ones after it, so the primary choice —
   * which is an index — has to move with them, or the client silently ends up
   * with a different primary account than the one they picked.
   */
  const handleRemoveAccount = useCallback((index) => {
    setFormData((prev) => {
      const removedPosition = index + 1;
      let primaryAccountIndex = prev.primaryAccountIndex;
      if (primaryAccountIndex === removedPosition) {
        primaryAccountIndex = 0;
      } else if (primaryAccountIndex > removedPosition) {
        primaryAccountIndex -= 1;
      }
      return {
        ...prev,
        extraAccounts: prev.extraAccounts.filter((_, i) => i !== index),
        primaryAccountIndex,
      };
    });
    setValidationErrors((prev) => {
      const newErrors = {};
      Object.entries(prev).forEach(([key, value]) => {
        if (!key.startsWith("extraAccount_")) {
          newErrors[key] = value;
        }
      });
      return newErrors;
    });
  }, []);

  const handleSetPrimaryAccount = useCallback((position) => {
    setFormData((prev) => ({ ...prev, primaryAccountIndex: position }));
  }, []);

  const toggleOpenFileDialog = useCallback(
    // eslint-disable-next-line no-shadow
    () => setOpenFileDialog((openFileDialog) => !openFileDialog),
    []
  );

  const handleDropZoneDrop = useCallback(
    async (acceptedFiles) => {
      setIsFileUploadLoading(true);
      try {
        const uploadedFiles = [...formData.uploadedFiles];
        for (let i = 0; i < acceptedFiles.length; i++) {
          const { data } = await uploadWithFormKey(acceptedFiles[i], formKey);
          uploadedFiles.push(data);
        }
        handleChange("uploadedFiles")(uploadedFiles);
      } catch (e) {
        console.log(e);
      }
      setIsFileUploadLoading(false);
    },
    [formKey, formData.uploadedFiles, handleChange]
  );

  const handleFileDelete = useCallback(
    (fileId) => {
      const updatedFiles = formData.uploadedFiles.filter(
        ({ id }) => id !== fileId
      );
      handleChange("uploadedFiles")(updatedFiles);
    },
    [formData.uploadedFiles, handleChange]
  );

  const validateForm = () => {
    const errors = {};

    // Helper to check max length
    const checkLength = (field, value, max, key) => {
      if (value && value.length > max) {
        errors[field] = t(`customer_form-${key}_too_long`, { max });
      }
    };
    // name (varchar 255, NOT NULL)
    if (!formData.name) {
      errors.name = t("customer_form-name_required");
    } else {
      checkLength("name", formData.name, 255, "name");
    }
    // legal_name (varchar 255, NOT NULL)
    if (!formData.legal_name) {
      errors.legal_name = t("customer_form-legal_name_required");
    } else {
      checkLength("legal_name", formData.legal_name, 255, "legal_name");
    }
    // companyCode (varchar 250, NOT NULL)
    if (!formData.companyCode) {
      errors.companyCode = t("customer_form-companyCode_required");
    } else {
      checkLength("companyCode", formData.companyCode, 250, "companyCode");
    }

    // phone (varchar 20, NOT NULL)
    if (!formData.phone) {
      errors.phone = t("customer_form-phone_required");
    } else {
      if (!validatePhone(formData.phone)) {
        errors.phone = t("customer_form-phone_invalid_format");
      }
      checkLength("phone", formData.phone, 20, "phone");
    }
    // email (varchar 255, nullable)
    if (!formData.email) {
      errors.email = t("customer_form-valid_email_required");
    } else {
      checkLength("email", formData.email, 255, "email");
      if (!validateEmail(formData.email)) {
        errors.email = t("customer_form-valid_email_required");
      }
    }

    // bankName (varchar 100, NOT NULL)
    if (!formData.bankName) {
      errors.bankName = t("customer_form-bankName_required");
    } else {
      checkLength("bankName", formData.bankName, 100, "bankName");
    }

    // IBAN / alternative account number rule:
    // at least one of iban or iban2 must be filled
    if (!formData.iban && !formData.iban2) {
      const msg = t("customer_form-iban_or_iban2_required");
      errors.iban = msg;
      errors.iban2 = msg;
    } else {
      if (formData.iban) {
        const ibanError = validateIBAN(formData.iban);
        if (ibanError) {
          errors.iban = ibanError;
        }
        checkLength("iban", formData.iban, 100, "iban");
      }
      if (formData.iban2) {
        // Alternative account number: only enforce max length, no IBAN format
        checkLength("iban2", formData.iban2, 100, "iban2");
      }
    }

    // currency of the first account (optional, but a code when given)
    if (formData.currency && formData.currency.length !== 3) {
      errors.currency = t("customer_form-currency_invalid");
    }
    checkLength("comment", formData.comment, 255, "comment");

    // 2nd, 3rd… account: same rules as the first one, per block.
    (formData.extraAccounts || []).forEach((account, index) => {
      const key = (field) => `extraAccount_${index}_${field}`;

      if (!account.bankName) {
        errors[key("bankName")] = t("customer_form-bankName_required");
      } else if (account.bankName.length > 100) {
        errors[key("bankName")] = t("customer_form-bankName_too_long", {
          max: 100,
        });
      }

      if (!account.iban && !account.iban2) {
        const msg = t("customer_form-iban_or_iban2_required");
        errors[key("iban")] = msg;
        errors[key("iban2")] = msg;
      } else {
        if (account.iban) {
          const ibanError = validateIBAN(account.iban);
          if (ibanError) {
            errors[key("iban")] = ibanError;
          } else if (account.iban.length > 100) {
            errors[key("iban")] = t("customer_form-iban_too_long", { max: 100 });
          }
        }
        if (account.iban2 && account.iban2.length > 100) {
          errors[key("iban2")] = t("customer_form-iban2_too_long", { max: 100 });
        }
      }

      if (account.swiftCode && account.swiftCode.length > 50) {
        errors[key("swiftCode")] = t("customer_form-swiftCode_too_long", {
          max: 50,
        });
      }

      if (account.currency && account.currency.length !== 3) {
        errors[key("currency")] = t("customer_form-currency_invalid");
      }

      if (account.comment && account.comment.length > 255) {
        errors[key("comment")] = t("customer_form-comment_too_long", {
          max: 255,
        });
      }
    });
    if (!formData.city) errors.city = t("customer_form-city_required");
    if (!formData.street) errors.street = t("customer_form-street_required");
    if (!formData.country) errors.country = t("customer_form-country_required");
    if (!formData.zipCodeLetters)
      errors.zipCodeLetters = t("customer_form-zipCode_letters_required");
    if (!formData.zipCodeFigures)
      errors.zipCodeFigures = t("customer_form-zipCode_figures_required");
    // signature contact
    if (!formData.signatureFullName) {
      errors.signatureFullName = t("customer_form-signature_fullName_required");
    } else {
      checkLength(
        "signatureFullName",
        formData.signatureFullName,
        100,
        "signatureFullName"
      );
    }
    if (!formData.signaturePosition) {
      errors.signaturePosition = t("customer_form-signature_position_required");
    } else {
      checkLength(
        "signaturePosition",
        formData.signaturePosition,
        100,
        "signaturePosition"
      );
    }
    if (!formData.signatureEmail || !validateEmail(formData.signatureEmail)) {
      errors.signatureEmail = t("customer_form-signature_valid_email_required");
    } else {
      checkLength(
        "signatureEmail",
        formData.signatureEmail,
        255,
        "signatureEmail"
      );
    }

    if (!formData.signatureBasisOfRepresentation)
      errors.signatureBasisOfRepresentation = t(
        "customer_form-signature_basis_of_representation_required"
      );

    // customer contact
    if (!formData.contactFullName)
      errors.contactFullName = t("customer_form-contact_fullName_required");
    else
      checkLength(
        "contactFullName",
        formData.contactFullName,
        50,
        "contactFullName"
      );

    if (!formData.contactPhone)
      errors.contactPhone = t("customer_form-contact_phone_required");
    else checkLength("contactPhone", formData.contactPhone, 20, "contactPhone");

    if (!formData.contactPosition)
      errors.contactPosition = t("customer_form-contact_position_required");
    else
      checkLength(
        "contactPosition",
        formData.contactPosition,
        255,
        "contactPosition"
      );

    if (!formData.contactEmail || !validateEmail(formData.contactEmail))
      errors.contactEmail = t("customer_form-contact_valid_email_required");
    else
      checkLength("contactEmail", formData.contactEmail, 255, "contactEmail");

    // signer
    if (!formData.invoiceEmail || !validateEmail(formData.invoiceEmail))
      errors.invoiceEmail = t("customer_form-invoice_valid_email_required");
    // invoiceEmail2 is optional - only validate if a value is provided (non-whitespace)
    const invoiceEmail2Trimmed = formData.invoiceEmail2 ? formData.invoiceEmail2.trim() : "";
    if (invoiceEmail2Trimmed !== "" && !validateEmail(invoiceEmail2Trimmed))
      errors.invoiceEmail2 = t("customer_form-invoice_secondary_email_invalid");
    if (!formData.signerPosition)
      errors.signerPosition = t("customer_form-signer_position_required");
    if (!formData.signerFullName)
      errors.signerFullName = t("customer_form-signer_firstName_required");

    if (!formData.isFileUploadOptional && formData.uploadedFiles.length === 0) {
      errors.documents = t("customer_form-documents_required");
    }

    // optional field errors
    if (formData.iban2) {
      checkLength("iban2", formData.iban2, 100, "iban2");
      const iban2Error = validateIBAN(formData.iban2);
      if (iban2Error) {
        errors.iban2 = iban2Error;
      }
    }

    if (formData.isVatPayer === null || formData.isVatPayer === undefined) {
      errors.isVatPayer = t("customer_form-isVatPayer_required");
    }

    // VAT code validation
    if (formData.isVatPayer) {
      const vatCodeErrors = validateVatCode(
        formData.vatCodeLetters,
        formData.vatCodeFigures,
        t
      );
      if (vatCodeErrors) {
        if (vatCodeErrors.vatCodeLetters) {
          errors.vatCodeLetters = vatCodeErrors.vatCodeLetters;
        }
        if (vatCodeErrors.vatCodeFigures) {
          errors.vatCodeFigures = vatCodeErrors.vatCodeFigures;
        }
      }
    }

    if (formData.vatCodeLetters) {
      checkLength(
        "vatCodeLetters",
        formData.vatCodeLetters,
        2,
        "vatCodeLetters"
      );
    }
    if (formData.vatCodeFigures) {
      checkLength(
        "vatCodeFigures",
        formData.vatCodeFigures,
        50,
        "vatCodeFigures"
      );
    }
    if (formData.website) {
      checkLength("website", formData.website, 255, "website");
    }

    if (formData.swiftCode) {
      checkLength("swiftCode", formData.swiftCode, 50, "swiftCode");
    }

    if (formData.officeAddress) {
      checkLength(
        "officeAddress",
        formData.officeAddress,
        255,
        "officeAddress"
      );
    }

    if (formData.region) {
      checkLength("region", formData.region, 255, "region");
    }

    if (formData.houseNumber) {
      checkLength("houseNumber", formData.houseNumber, 255, "houseNumber");
    }

    if (formData.officeNumber) {
      checkLength("officeNumber", formData.officeNumber, 255, "officeNumber");
    }

    if (!isAgreeDeliverElectronically && !isDisagreeDeliverElectronically) {
      errors.deliveryError = true;
    }

    const agree = isAgreeDeliverElectronically;
    const disagree = isDisagreeDeliverElectronically;
    if ((agree && disagree) || (!agree && !disagree)) {
      errors.deliveryError = true;
    }

    return errors;
  };

  /** After validation fails, scroll so users see why submit did not run (errors are often above the sticky/footer area). */
  const scrollToFirstValidationIssue = useCallback((errors) => {
    if (!errors || Object.keys(errors).length === 0) return;
    requestAnimationFrame(() => {
      const invalidInput = document.querySelector(
        '.Polaris-TextField__Input[aria-invalid="true"], .Polaris-Select__Input[aria-invalid="true"]'
      );
      if (invalidInput) {
        invalidInput.scrollIntoView({ behavior: "smooth", block: "center" });
        if (typeof invalidInput.focus === "function") {
          try {
            invalidInput.focus({ preventScroll: true });
          } catch {
            invalidInput.focus();
          }
        }
        return;
      }
      if (errors.deliveryError) {
        document
          .getElementById("customer-form-section-electronic-invoices")
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      if (errors.documents) {
        document
          .getElementById("customer-form-section-documents")
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
  }, []);

  const handleSubmit = async () => {
    setFormSubmitLoading(true);
    const errors = validateForm();

    const payload = {
      ...formData,
      houseNumber: formData.houseNumber || null,
      street: formData.street || null,
      city: formData.city || null,
      region: formData.region || null,
      zipCodeLetters: formData.zipCodeLetters || null,
      zipCodeFigures: formData.zipCodeFigures || null,
      country: formData.country || null,
      address: `${formData.houseNumber || ""}, ${formData.street || ""}, ${
        formData.officeNumber || ""
      }, ${formData.city}, ${formData.zipCodeLetters}${
        formData.zipCodeFigures
      }, ${formData.country}`,
      legalName: formData.legal_name || null, // Map legal_name to legalName for backend
      phone:
        formData.countryCode && formData.phone
          ? `${formData.countryCode}${formData.phone}`
          : formData.phone,
      // Combine VAT code letters and figures into vatCode for backend
      vatCode:
        formData.isVatPayer && formData.vatCodeLetters && formData.vatCodeFigures
          ? `${formData.vatCodeLetters}${formData.vatCodeFigures}`
          : formData.isVatPayer ? (formData.vatCodeLetters || formData.vatCodeFigures || "") : "",
      isVatPayer: formData.isVatPayer,
      isAgreeDeliverElectronically,
      isDisagreeDeliverElectronically,
      // The full list. `iban` / `iban2` / `bankName` / `swiftCode` above stay
      // the first account, so a backend or a PDF that only knows the flat
      // fields still sees a complete, valid questionnaire.
      accounts: [
        {
          iban: formData.iban,
          iban2: formData.iban2,
          bankName: formData.bankName,
          swiftCode: formData.swiftCode,
          currency: formData.currency,
          comment: formData.comment,
          isPrimary: formData.primaryAccountIndex === 0,
        },
        ...(formData.extraAccounts || []).map((account, index) => ({
          iban: account.iban,
          iban2: account.iban2,
          bankName: account.bankName,
          swiftCode: account.swiftCode,
          currency: account.currency,
          comment: account.comment,
          isPrimary: formData.primaryAccountIndex === index + 1,
        })),
      ],
    };

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setShowFormError(true);
      scrollToFirstValidationIssue(errors);
      setFormSubmitLoading(false);
      // Persist attempt for diagnostics; do not block partner UX if this fails
      try {
        await postQuestionnaireValidationAttempt(formKey, {
          payload,
          validationErrors: errors,
        });
      } catch (e) {
        console.error(e);
      }
      return;
    }
    setShowFormError(false);

    try {
      await axios.put(`${API_URL}/customers/form-key/${formKey}`, payload);
      // Add success handling here if needed
      setFormSubmitLoading(false);
      setIsSuccess(true);
    } catch (e) {
      setFormSubmitLoading(false);
      setShowFormError(true);
      console.error(e);
      // Add error handling here if needed
    }
  };

  useEffect(() => {
    const fetchCustomerData = async () => {
      try {
        const customerEntity = await axios
          .get(`${API_URL}/customers/form-key/${formKey}`)
          .then((response) => response.data);

        if (customerEntity) {
          setFormData(mapCustomerEntityToFormData(customerEntity));
        } else {
          setIsError(true);
        }
        setIsLoading(false);
      } catch (e) {
        setIsError(true);
        console.error(e);
      }
    };

    fetchCustomerData();
  }, [formKey]);

  if (isError) {
    return (
      <FormOutcome
        Icon={AlertCircle}
        iconClass="text-red-600"
        heading={t("customer_form-error_heading")}
        text={t("customer_form-error_message")}
      />
    );
  }

  if (isLoading) {
    return <Loading />;
  }

  if (isSuccess) {
    return (
      <FormOutcome
        Icon={CheckCircle}
        iconClass="text-green-600"
        heading={t("customer_form-success_heading")}
        text={t("customer_form-success_text")}
      />
    );
  }

  return (
    <>
      {APP_CONFIG.isUhbGroup && (
        <>
          <div
            style={{
              maxWidth: "950px",
              margin: "0 auto",
              padding: "10px 0",
              textAlign: "center",
            }}
          >
            <img
              src="https://uhb.lt/uhb-group-logo.png"
              alt="UHB Group"
              width="207"
              height="46"
            />
          </div>
          <hr />
        </>
      )}
      <Page>
        <div className="section_heading_">
          <InlineStack align="center">
            <Text fontWeight="bold" variant="headingXl">
              {formData?.companyName
                ? `${formData.companyName.toUpperCase()} `
                : ""}
              PARTNERIO ANKETA / PARTNER PROFILE FORM
            </Text>
          </InlineStack>
        </div>
        <br />
        <div className="questionnaire-form questionnaire-form--public">
          <Form onSubmit={handleSubmit}>
            <FormLayout>
              <Card roundedAbove="sm">
                <BlockStack gap="200">
                  <InlineGrid columns="1fr auto">
                    <div className="section_heading_">
                      <Text as="h2" variant="headingSm">
                        1.{t("customer_form-general_info")}
                      </Text>
                    </div>
                  </InlineGrid>
                  {/* first – label left, box right (horizontal layout) */}
                  <BlockStack gap="500">
                  {/* first – label left, box right; use LegacyStack so box aligns with Company code row */}
                    <LegacyStack wrap={false}>
                      <LegacyStack.Item fill>
                        <label htmlFor="customer-form-legal-name">
                          {t(" Teisinė forma ir pavadinimas / Legal form and Company name")}:
                        </label>
                      </LegacyStack.Item>
                      <div className="form-row-single-field min-w-0 w-full">
                        {/* Errors render below this bordered row (not inside), so they do not overlap the frame */}
                        <div className="legal-company-one-box min-h-8 w-full min-w-0">
                          <div className="legal-company-one-box__cell flex min-h-8 min-w-0 flex-col border-r border-[#c9c9c9] px-0 py-0">
                            <div className="legal-company-one-box__field-wrap flex min-h-8 min-w-0 flex-1 flex-col justify-start">
                              <TextField
                                align="left"
                                id="customer-form-legal-name"
                                variant="borderless"
                                label={t("legal_name")}
                                value={formData.legal_name}
                                onChange={handleChange("legal_name")}
                                requiredIndicator
                                error={Boolean(validationErrors.legal_name)}
                                labelHidden
                              />
                            </div>
                          </div>
                          <div className="legal-company-one-box__cell flex min-h-8 min-w-0 flex-col px-0 py-0">
                            <div className="legal-company-one-box__field-wrap flex min-h-8 min-w-0 flex-1 flex-col justify-start">
                              <TextField
                                align="left"
                                id="customer-form-name"
                                variant="borderless"
                                label={t("customer_form-name")}
                                value={formData.name}
                                onChange={handleChange("name")}
                                requiredIndicator
                                error={Boolean(validationErrors.name)}
                                labelHidden
                              />
                            </div>
                          </div>
                        </div>
                        <div className="legal-company-one-box__errors mt-1 w-full min-w-0">
                          <div className="min-w-0 pr-2">
                            <InlineError
                              message={validationErrors.legal_name}
                              fieldID="customer-form-legal-name"
                            />
                          </div>
                          <div className="min-w-0 pl-2">
                            <InlineError
                              message={validationErrors.name}
                              fieldID="customer-form-name"
                            />
                          </div>
                        </div>
                        <div className="legal-company-one-box__hints mt-1 text-xs text-[#6d7175]">
                          <span className="min-w-0 flex-1 break-words pr-2">
                            {t("(pvz. UAB, AB, MB, IV, kt. / e.g., LLC, SIA, Sp. z o.o., etc.)")}
                          </span>
                          <span className="min-w-0 flex-1 break-words pl-2">
                            {t("(įmonės tikslus pavadinimas / Exact company name)")}
                          </span>
                        </div>
                      </div>
                    </LegacyStack>
                    {/* Company code */}
                    <LegacyStack wrap={false}>
                      <LegacyStack.Item fill>
                        <label htmlFor="customer-form-companyCode">
                          {t("Įmonės kodas/ Company code")}:
                        </label>
                      </LegacyStack.Item>
                      <div className="form-row-single-field">
                        <TextField
                          id="customer-form-companyCode"
                          label={`${t("customer_form-companyCode")}`}
                          value={formData.companyCode}
                          onChange={handleChange("companyCode")}
                          requiredIndicator
                          error={validationErrors.companyCode}
                          helpText={t("customer_form-companyCode_help")}
                          labelHidden
                        />
                      </div>
                    </LegacyStack>
                    {/* Is VAT payer? */}
                    <LegacyStack wrap={false}>
                      <LegacyStack.Item fill>
                        {t("Ar esate PVM mokėtojas? / Are you a VAT payer?")}:
                      </LegacyStack.Item>
                      <div className="form-row-single-field">
                        <LegacyStack gap="loose">
                          <Checkbox
                            label={t("Taip / Yes")}
                            checked={formData.isVatPayer === true}
                            onChange={(value) => {
                              if (value) handleChange("isVatPayer")(true);
                              else handleChange("isVatPayer")(null);
                            }}
                          />
                          <Checkbox
                            label={t("Ne / No")}
                            checked={formData.isVatPayer === false}
                            onChange={(value) => {
                              if (value) handleChange("isVatPayer")(false);
                              else handleChange("isVatPayer")(null);
                            }}
                          />
                        </LegacyStack>
                        {validationErrors.isVatPayer && (
                          <div style={{ marginTop: "4px" }}>
                            <InlineError
                              message={validationErrors.isVatPayer}
                              fieldID="isVatPayer"
                            />
                          </div>
                        )}
                      </div>
                    </LegacyStack>

                    {/* VAT code */}
                    {formData.isVatPayer && (
                      <LegacyStack wrap={false}>
                        <LegacyStack.Item fill>
                          <label htmlFor="customer-form-vatCodeLetters">
                            {t("PVM kodas/VAT code")}:
                          </label>
                        </LegacyStack.Item>
                        <div className="form-row-two-fields">
                          <Select
                            id="customer-form-vatCodeLetters"
                            label={t("customer_form-vatCode_country")}
                            options={getAllCountryISOptions(t)}
                            value={formData.vatCodeLetters}
                            onChange={handleChange("vatCodeLetters")}
                            requiredIndicator
                            error={validationErrors.vatCodeLetters}
                            labelHidden
                            helpText={t("(Šalis / Country)")}
                          />
                          <TextField
                            label={t("customer_form-vatCode")}
                            value={formData.vatCodeFigures}
                            onChange={handleChange("vatCodeFigures")}
                            requiredIndicator
                            error={validationErrors.vatCodeFigures}
                            autoComplete="off"
                            helpText={t("(Kodas / Code)")}
                            labelHidden
                          />
                        </div>
                      </LegacyStack>
                    )}

                  </BlockStack>
                  {/* second */}
                  <InlineGrid columns="1fr auto">
                    <Text as="h2" variant="bodyMd" fontWeight="semibold">
                      {t("customer_form-reg_address")}
                    </Text>
                  </InlineGrid>
                  <BlockStack gap="500">
                    <LegacyStack>
                      <LegacyStack.Item fill>
                        <label htmlFor="customer-form-country">
                          {t(
                            "Šalis (kur registruota)/ Country (of registration)"
                          )}
                          :
                        </label>
                      </LegacyStack.Item>

                      <Select
                        id="customer-form-country"
                        label={`${t("customer_form-country")}`}
                        options={getAllCountryOptions(t)}
                        onChange={handleChange("country")}
                        value={formData.country}
                        requiredIndicator
                        error={validationErrors.country}
                        helpText={`${t("customer_form-country_help")}`}
                        labelHidden
                      />
                    </LegacyStack>

                    <LegacyStack>
                      <LegacyStack.Item fill>
                        <label htmlFor="customer-form-region">
                          {t("Rajonas/ District")}:
                        </label>
                      </LegacyStack.Item>

                      <TextField
                        id="customer-form-region"
                        label={`${t("Rajonas/ District")}`}
                        value={formData.region}
                        onChange={handleChange("region")}
                        error={validationErrors.region}
                        labelHidden
                      />
                    </LegacyStack>

                    <LegacyStack>
                      <LegacyStack.Item fill>
                        <label htmlFor="customer-form-city">
                          {t("Miestas/ City")}:
                        </label>
                      </LegacyStack.Item>

                      <TextField
                        id="customer-form-city"
                        label={`${t("Miestas/ City")}`}
                        value={formData.city}
                        onChange={handleChange("city")}
                        requiredIndicator
                        error={validationErrors.city}
                        helpText={t("customer_form-city_help")}
                        labelHidden
                      />
                    </LegacyStack>
                    <LegacyStack>
                      <LegacyStack.Item fill>
                        <label htmlFor="customer-form-street">
                          {t("Gatvė/ Street")}:
                        </label>
                      </LegacyStack.Item>

                      <TextField
                        id="customer-form-street"
                        label={`${t("Gatvė/ Street")}`}
                        value={formData.street}
                        onChange={handleChange("street")}
                        requiredIndicator
                        error={validationErrors.street}
                        labelHidden
                      />
                    </LegacyStack>
                    <LegacyStack>
                      <LegacyStack.Item fill>
                        <label htmlFor="customer-form-houseNumber">
                          {t("Namo numeris / House number")}:
                        </label>
                      </LegacyStack.Item>

                      <TextField
                        id="customer-form-houseNumber"
                        label={`${t("Namo numeris / House number")}`}
                        value={formData.houseNumber}
                        onChange={handleChange("houseNumber")}
                        error={validationErrors.houseNumber}
                        labelHidden
                      />
                    </LegacyStack>

                    <LegacyStack>
                      <LegacyStack.Item fill>
                        <label htmlFor="customer-form-officeNumber">
                          {t("Ofiso numeris / Office number (if applicable)")}:
                        </label>
                      </LegacyStack.Item>

                      <TextField
                        id="customer-form-officeNumber"
                        label={`${t(
                          "Ofiso numeris / Office number (if applicable)"
                        )}`}
                        value={formData.officeNumber}
                        onChange={handleChange("officeNumber")}
                        error={validationErrors.officeNumber}
                        labelHidden
                      />
                    </LegacyStack>
                    <LegacyStack>
                      <LegacyStack.Item fill>
                        <label htmlFor="customer-form-zipCodeLetters">
                          {t("Pašto kodas/ Postal code")}:
                        </label>
                      </LegacyStack.Item>

                      <div className="form-row-two-fields">
                        <TextField
                          id="customer-form-zipCodeLetters"
                          label={`Raidės / Letters`}
                          value={formData.zipCodeLetters}
                          onChange={handleChange("zipCodeLetters")}
                          requiredIndicator
                          error={validationErrors.zipCodeLetters}
                          autoComplete="off"
                          helpText={t("(Raidės / Letters)")}
                          labelHidden
                        />
                        <TextField
                          label={`Skaičiai / Figures`}
                          value={formData.zipCodeFigures}
                          onChange={handleChange("zipCodeFigures")}
                          requiredIndicator
                          error={validationErrors.zipCodeFigures}
                          autoComplete="off"
                          helpText={t("(Skaičiai / Figures)")}
                          labelHidden
                        />
                      </div>
                    </LegacyStack>
                    <LegacyStack>
                      <LegacyStack.Item fill>
                        <label htmlFor="customer-form-officeAddress">
                          {t(
                            "Faktinis (veiklos) adresas/ Actual business address"
                          )}
                          :
                        </label>
                      </LegacyStack.Item>

                      <TextField
                        id="customer-form-officeAddress"
                        label={`${t(
                          "Faktinis (veiklos) adresas/ Actual business address"
                        )}`}
                        value={formData.officeAddress}
                        onChange={handleChange("officeAddress")}
                        error={validationErrors.officeAddress}
                        helpText={t(
                          "(nurodoma jeigu skiriasi adresas/ to be provided if it differs from the registered address)"
                        )}
                        labelHidden
                      />
                    </LegacyStack>
                    <LegacyStack>
                      <LegacyStack.Item fill>
                        <label htmlFor="customer-form-phone">
                          {t("Tel. Nr./ Phone number")}:
                        </label>
                      </LegacyStack.Item>

                      <div className="form-row-phone">
                        <Select
                          label={`Šalies kodas / Country Code`}
                          options={Object.entries(countryCodes).map(
                            ([iso, dial]) => ({
                              value: dial,
                              label: `${iso} (${dial})`,
                            })
                          )}
                          value={formData.countryCode}
                          onChange={handleChange("countryCode")}
                          //helpText={t("Šalies kodas / Country Code")}
                          labelHidden
                        />
                        <TextField
                          id="customer-form-phone"
                          label={`Telefono numeris / Phone Number`}
                          value={formData.phone}
                          onChange={handleChange("phone")}
                          requiredIndicator
                          error={validationErrors.phone}
                          autoComplete="off"
                          helpText={t("(Telefono numeris / Phone Number)")}
                          labelHidden
                        />
                      </div>
                    </LegacyStack>

                    <LegacyStack>
                      <LegacyStack.Item fill>
                        <label htmlFor="customer-form-email">
                          {t("El. paštas/ E-mail")}:
                        </label>
                      </LegacyStack.Item>

                      <TextField
                        id="customer-form-email"
                        label={`${t("El. paštas/ E-mail")}`}
                        value={formData.email}
                        onChange={handleChange("email")}
                        requiredIndicator
                        error={validationErrors.email}
                        helpText={t("customer_form-email_help")}
                        labelHidden
                      />
                    </LegacyStack>

                    <LegacyStack>
                      <LegacyStack.Item fill>
                        <label htmlFor="customer-form-website">
                          {t("customer_form-website")}:
                        </label>
                      </LegacyStack.Item>

                      <TextField
                        id="customer-form-website"
                        label={`${t("customer_form-website")}`}
                        value={formData.website}
                        error={validationErrors.website}
                        onChange={handleChange("website")}
                        helpText={t("customer_form-website_help")}
                        labelHidden
                      />
                    </LegacyStack>
                  </BlockStack>
                </BlockStack>
              </Card>

              {/* third */}
              <Card roundedAbove="sm">
                <BlockStack gap="200">
                  <div className="section_heading_">
                    <InlineGrid columns="1fr auto">
                      <Text as="h2" variant="headingSm">
                        2.
                        {t("Banko duomenys / Bank details")}
                      </Text>
                    </InlineGrid>
                  </div>
                  <BlockStack gap="500">
                    <LegacyStack>
                      <LegacyStack.Item fill>
                        <label htmlFor="customer-form-iban">
                          {t("customer_form-iban")}:
                        </label>
                      </LegacyStack.Item>

                      <TextField
                        id="customer-form-iban"
                        label={`${t("customer_form-iban")}`}
                        value={formData.iban}
                        onChange={handleChange("iban")}
                        error={validationErrors.iban}
                        helpText={t("customer_form-iban_help")}
                        labelHidden
                      />
                    </LegacyStack>
                    <LegacyStack>
                      <LegacyStack.Item fill>
                        <label htmlFor="customer-form-iban2">
                          {t(
                            "*Kitas ne IBAN Nr./ Alternative account number (if not IBAN):"
                          )}
                        </label>
                      </LegacyStack.Item>
                      <TextField
                        id="customer-form-iban2"
                        label={`${t(
                          "*Kitas ne IBAN Nr./ Alternative account number (if not IBAN):"
                        )}`}
                        value={formData.iban2}
                        onChange={handleChange("iban2")}
                        error={validationErrors.iban2}
                        helpText={t(
                          "(pildoma tik tuo atveju, jeigu nėra IBAN/ to be completed only if no IBAN is available)"
                        )}
                        placeholder={t("customer_form-iban2_placeholder")}
                        labelHidden
                      />
                    </LegacyStack>
                    <LegacyStack>
                      <LegacyStack.Item fill>
                        <label htmlFor="customer-form-bankName">
                          {t("customer_form-bankName")}:
                        </label>
                      </LegacyStack.Item>

                      <TextField
                        id="customer-form-bankName"
                        label={`${t("customer_form-bankName")}`}
                        value={formData.bankName}
                        onChange={handleChange("bankName")}
                        requiredIndicator
                        error={validationErrors.bankName}
                        helpText={t("customer_form-bankName_help")}
                        labelHidden
                      />
                    </LegacyStack>
                    <LegacyStack>
                      <LegacyStack.Item fill>
                        <label htmlFor="customer-form-swiftCode">
                          {t("customer_form-swiftCode")}:
                        </label>
                      </LegacyStack.Item>
                      <TextField
                        id="customer-form-swiftCode"
                        label={`${t("customer_form-swiftCode")}`}
                        value={formData.swiftCode}
                        onChange={handleChange("swiftCode")}
                        error={validationErrors.swiftCode}
                        helpText={t("customer_form-swiftCode_help")}
                        labelHidden
                      />
                    </LegacyStack>
                    <LegacyStack>
                      <LegacyStack.Item fill>
                        <label htmlFor="customer-form-currency">
                          {t("customer_form-currency")}:
                        </label>
                      </LegacyStack.Item>
                      <BlockStack gap="150">
                        <TextField
                          id="customer-form-currency"
                          label={`${t("customer_form-currency")}`}
                          value={formData.currency}
                          onChange={handleChange("currency")}
                          error={validationErrors.currency}
                          helpText={t("customer_form-currency_help")}
                          maxLength={3}
                          labelHidden
                        />
                        <InlineStack gap="200">
                          {POPULAR_CURRENCIES.map((code) => (
                            <Button
                              key={code}
                              size={currencyButtonSize}
                              variant={
                                formData.currency === code
                                  ? "primary"
                                  : "secondary"
                              }
                              onClick={() => handleChange("currency")(code)}
                            >
                              {code}
                            </Button>
                          ))}
                        </InlineStack>
                      </BlockStack>
                    </LegacyStack>
                    <LegacyStack>
                      <LegacyStack.Item fill>
                        <label htmlFor="customer-form-comment">
                          {t("customer_form-account_comment")}:
                        </label>
                      </LegacyStack.Item>
                      <TextField
                        id="customer-form-comment"
                        label={`${t("customer_form-account_comment")}`}
                        value={formData.comment}
                        onChange={handleChange("comment")}
                        error={validationErrors.comment}
                        helpText={t("customer_form-account_comment_help")}
                        labelHidden
                      />
                    </LegacyStack>

                    {(formData.extraAccounts || []).length > 0 && (
                      <RadioButton
                        label={t("customer_form-account_primary")}
                        helpText={t("customer_form-account_primary_help")}
                        id="customer-form-primary-account-0"
                        name="customerFormPrimaryAccount"
                        checked={formData.primaryAccountIndex === 0}
                        onChange={() => handleSetPrimaryAccount(0)}
                      />
                    )}

                    {(formData.extraAccounts || []).map((account, index) => (
                      <Box
                        key={`extra-account-${index}`}
                        padding="400"
                        background="bg-surface-secondary"
                        borderRadius="200"
                      >
                        <BlockStack gap="400">
                          <InlineGrid columns="1fr auto">
                            <Text as="h3" variant="headingSm">
                              {t("customer_form-account_heading", {
                                number: index + 2,
                              })}
                            </Text>
                            <Button
                              variant="plain"
                              tone="critical"
                              onClick={() => handleRemoveAccount(index)}
                            >
                              {t("customer_form-account_remove")}
                            </Button>
                          </InlineGrid>

                          <LegacyStack>
                            <LegacyStack.Item fill>
                              <label htmlFor={`customer-form-account-${index}-iban`}>
                                {t("customer_form-iban")}:
                              </label>
                            </LegacyStack.Item>
                            <TextField
                              id={`customer-form-account-${index}-iban`}
                              label={`${t("customer_form-iban")}`}
                              value={account.iban}
                              onChange={handleExtraAccountChange(index, "iban")}
                              error={
                                validationErrors[`extraAccount_${index}_iban`]
                              }
                              labelHidden
                            />
                          </LegacyStack>
                          <LegacyStack>
                            <LegacyStack.Item fill>
                              <label htmlFor={`customer-form-account-${index}-iban2`}>
                                {t(
                                  "*Kitas ne IBAN Nr./ Alternative account number (if not IBAN):"
                                )}
                              </label>
                            </LegacyStack.Item>
                            <TextField
                              id={`customer-form-account-${index}-iban2`}
                              label={`${t(
                                "*Kitas ne IBAN Nr./ Alternative account number (if not IBAN):"
                              )}`}
                              value={account.iban2}
                              onChange={handleExtraAccountChange(index, "iban2")}
                              error={
                                validationErrors[`extraAccount_${index}_iban2`]
                              }
                              labelHidden
                            />
                          </LegacyStack>
                          <LegacyStack>
                            <LegacyStack.Item fill>
                              <label htmlFor={`customer-form-account-${index}-bankName`}>
                                {t("customer_form-bankName")}:
                              </label>
                            </LegacyStack.Item>
                            <TextField
                              id={`customer-form-account-${index}-bankName`}
                              label={`${t("customer_form-bankName")}`}
                              value={account.bankName}
                              onChange={handleExtraAccountChange(
                                index,
                                "bankName"
                              )}
                              requiredIndicator
                              error={
                                validationErrors[
                                  `extraAccount_${index}_bankName`
                                ]
                              }
                              labelHidden
                            />
                          </LegacyStack>
                          <LegacyStack>
                            <LegacyStack.Item fill>
                              <label htmlFor={`customer-form-account-${index}-swiftCode`}>
                                {t("customer_form-swiftCode")}:
                              </label>
                            </LegacyStack.Item>
                            <TextField
                              id={`customer-form-account-${index}-swiftCode`}
                              label={`${t("customer_form-swiftCode")}`}
                              value={account.swiftCode}
                              onChange={handleExtraAccountChange(
                                index,
                                "swiftCode"
                              )}
                              error={
                                validationErrors[
                                  `extraAccount_${index}_swiftCode`
                                ]
                              }
                              labelHidden
                            />
                          </LegacyStack>
                          <LegacyStack>
                            <LegacyStack.Item fill>
                              <label htmlFor={`customer-form-account-${index}-currency`}>
                                {t("customer_form-currency")}:
                              </label>
                            </LegacyStack.Item>
                            <BlockStack gap="150">
                              <TextField
                                id={`customer-form-account-${index}-currency`}
                                label={`${t("customer_form-currency")}`}
                                value={account.currency}
                                onChange={handleExtraAccountChange(
                                  index,
                                  "currency"
                                )}
                                error={
                                  validationErrors[
                                    `extraAccount_${index}_currency`
                                  ]
                                }
                                helpText={t("customer_form-currency_help")}
                                maxLength={3}
                                labelHidden
                              />
                              <InlineStack gap="200">
                                {POPULAR_CURRENCIES.map((code) => (
                                  <Button
                                    key={code}
                                    size={currencyButtonSize}
                                    variant={
                                      account.currency === code
                                        ? "primary"
                                        : "secondary"
                                    }
                                    onClick={() =>
                                      handleExtraAccountChange(
                                        index,
                                        "currency"
                                      )(code)
                                    }
                                  >
                                    {code}
                                  </Button>
                                ))}
                              </InlineStack>
                            </BlockStack>
                          </LegacyStack>
                          <LegacyStack>
                            <LegacyStack.Item fill>
                              <label htmlFor={`customer-form-account-${index}-comment`}>
                                {t("customer_form-account_comment")}:
                              </label>
                            </LegacyStack.Item>
                            <TextField
                              id={`customer-form-account-${index}-comment`}
                              label={`${t("customer_form-account_comment")}`}
                              value={account.comment}
                              onChange={handleExtraAccountChange(
                                index,
                                "comment"
                              )}
                              error={
                                validationErrors[`extraAccount_${index}_comment`]
                              }
                              helpText={t("customer_form-account_comment_help")}
                              labelHidden
                            />
                          </LegacyStack>

                          <RadioButton
                            label={t("customer_form-account_primary")}
                            id={`customer-form-primary-account-${index + 1}`}
                            name="customerFormPrimaryAccount"
                            checked={
                              formData.primaryAccountIndex === index + 1
                            }
                            onChange={() => handleSetPrimaryAccount(index + 1)}
                          />
                        </BlockStack>
                      </Box>
                    ))}

                    <InlineStack>
                      <Button onClick={handleAddAccount}>
                        {t("customer_form-account_add")}
                      </Button>
                    </InlineStack>
                  </BlockStack>
                </BlockStack>
              </Card>

              {/* fourth */}
              <Card roundedAbove="sm">
                <BlockStack gap="200">
                  <>
                    <div className="section_heading_">
                      <Text as="h2" variant="headingSm">
                        3.{`${t("customer_form-contacts")}`}
                      </Text>
                    </div>

                    <BlockStack gap="500">
                      <LegacyStack>
                        <LegacyStack.Item fill>
                          <label htmlFor="customer-form-contactFullName">
                            {t("Kontaktinis asmuo/ Contact Person")}:
                          </label>
                        </LegacyStack.Item>

                        <TextField
                          id="customer-form-contactFullName"
                          label={`${t("Kontaktinis asmuo/ Contact Person")}`}
                          value={formData.contactFullName}
                          onChange={handleChange("contactFullName")}
                          requiredIndicator
                          error={validationErrors.contactFullName}
                          helpText={t("(vardas, pavardė/ first and last name)")}
                          labelHidden
                        />
                      </LegacyStack>
                      <LegacyStack>
                        <LegacyStack.Item fill>
                          <label htmlFor="customer-form-contactPhone">
                            {t("Tel. Nr./ Phone number")}:
                          </label>
                        </LegacyStack.Item>
                        <TextField
                          id="customer-form-contactPhone"
                          label={`${t("Tel. Nr./ Phone number")}`}
                          value={formData.contactPhone}
                          onChange={handleChange("contactPhone")}
                          requiredIndicator
                          error={validationErrors.contactPhone}
                          helpText={t("customer_form-contact_phone_help")}
                          labelHidden
                        />
                      </LegacyStack>
                      <LegacyStack>
                        <LegacyStack.Item fill>
                          <label htmlFor="customer-form-contactPosition">
                            {t("Pareigos/ Position")}:
                          </label>
                        </LegacyStack.Item>

                        <TextField
                          id="customer-form-contactPosition"
                          label={`${t("Pareigos/ Position:")}`}
                          value={formData.contactPosition}
                          onChange={handleChange("contactPosition")}
                          requiredIndicator
                          error={validationErrors.contactPosition}
                          helpText={t("(pareigos / position)")}
                          labelHidden
                        />
                      </LegacyStack>
                      <LegacyStack>
                        <LegacyStack.Item fill>
                          <label htmlFor="customer-form-contactEmail">
                            {t("customer_form-contact_email")}:
                          </label>
                        </LegacyStack.Item>

                        <TextField
                          id="customer-form-contactEmail"
                          label={`${t("customer_form-contact_email")}`}
                          type="email"
                          value={formData.contactEmail}
                          onChange={handleChange("contactEmail")}
                          requiredIndicator
                          error={validationErrors.contactEmail}
                          helpText={t("customer_form-contact_email_help")}
                          labelHidden
                        />
                      </LegacyStack>
                    </BlockStack>
                  </>
                  <InlineGrid columns="1fr auto">
                    <Text as="h3" variant="headingSm">
                      {`${t("Vadovo duomenys/ Manager information")}`}
                    </Text>
                  </InlineGrid>
                  <Bleed marginInline="400">
                    <Box
                      background="bg-surface-secondary"
                      // paddingBlock="200"
                      paddingInline="400"
                    >
                      <BlockStack gap="500">
                        <LegacyStack>
                          <LegacyStack.Item fill>
                            <label htmlFor="customer-form-signatureFullName">
                              {t(
                                "Asmens kuris pasirašys sutartį/ Person authorized to execute the Contract"
                              )}
                              :
                            </label>
                          </LegacyStack.Item>
                          <TextField
                            id="customer-form-signatureFullName"
                            label={`${t("customer_form-signature_fullName")}`}
                            value={formData.signatureFullName}
                            onChange={handleChange("signatureFullName")}
                            requiredIndicator
                            error={validationErrors.signatureFullName}
                            helpText={t(
                              "(vardas, pavardė / first and last name)"
                            )}
                            labelHidden
                          />
                        </LegacyStack>
                        <LegacyStack>
                          <LegacyStack.Item fill>
                            <label htmlFor="customer-form-signaturePosition">
                              {t("Pareigos / Position")}:
                            </label>
                          </LegacyStack.Item>

                          <TextField
                            id="customer-form-signaturePosition"
                            label={`${t("Pareigos / Position")}`}
                            value={formData.signaturePosition}
                            onChange={handleChange("signaturePosition")}
                            requiredIndicator
                            error={validationErrors.signaturePosition}
                            helpText={t("(pareigos / position)")}
                            labelHidden
                          />
                        </LegacyStack>

                        <LegacyStack>
                          <LegacyStack.Item fill>
                            <label htmlFor="customer-form-signatureEmail">
                              {t("El. paštas/ E-mail")}:
                            </label>
                          </LegacyStack.Item>
                          <TextField
                            id="customer-form-signatureEmail"
                            label={`${t("El. paštas/ E-mail:")}`}
                            type="email"
                            value={formData.signatureEmail}
                            onChange={handleChange("signatureEmail")}
                            requiredIndicator
                            error={validationErrors.signatureEmail}
                            labelHidden
                          />
                        </LegacyStack>

                        <LegacyStack>
                          <LegacyStack.Item fill>
                            <label htmlFor="customer-form-signatureBasisOfRepresentation">
                              {t(
                                "Atstovavimo pagrindas/ Grounds for Representation"
                              )}
                              :
                            </label>
                          </LegacyStack.Item>
                          <TextField
                            id="customer-form-signatureBasisOfRepresentation"
                            label={`$`}
                            value={formData.signatureBasisOfRepresentation}
                            onChange={handleChange(
                              "signatureBasisOfRepresentation"
                            )}
                            requiredIndicator
                            error={
                              validationErrors.signatureBasisOfRepresentation
                            }
                            labelHidden
                          />
                          <div
                            className="input-wid_help_text"
                            style={{ whiteSpace: "pre-line" }}
                          >
                            {t(
                              "customer_form-signature_basis_of_representation_help"
                            )}
                          </div>
                        </LegacyStack>
                      </BlockStack>
                    </Box>
                  </Bleed>
                  <br />
                </BlockStack>
              </Card>
              {/* fifth */}
              <Card roundedAbove="sm">
                <BlockStack gap="200">
                  <div className="section_heading_">
                    <InlineGrid columns="1fr auto">
                      <Text as="h2" variant="headingSm">
                        4.
                        {`${t("Elektroninės sąskaitos / Electronic Invoices")}`}
                      </Text>
                    </InlineGrid>
                  </div>
                  <BlockStack gap="500">
                    <LegacyStack>
                      <LegacyStack.Item fill>
                        <label htmlFor="customer-form-invoiceEmail">
                          {t(
                            "El. paštas sąskaitų siuntimui/ E-mail for invoice submission"
                          )}
                          :
                        </label>
                      </LegacyStack.Item>
                      <TextField
                        id="customer-form-invoiceEmail"
                        label={`${t(
                          "El. paštas sąskaitų siuntimui/ E-mail for invoice submission"
                        )}`}
                        value={formData.invoiceEmail}
                        onChange={handleChange("invoiceEmail")}
                        helpText={t("customer_form-invoice_email_help")}
                        requiredIndicator
                        error={validationErrors.invoiceEmail}
                        labelHidden
                      />
                    </LegacyStack>

                    <LegacyStack>
                      <LegacyStack.Item fill>
                        <label htmlFor="customer-form-invoiceEmail2">
                          {t("Papildomas el. paštas / Secondary E-mail")}:
                        </label>
                      </LegacyStack.Item>
                      <TextField
                        id="customer-form-invoiceEmail2"
                        label={`${t(
                          "Papildomas el. paštas / Secondary E-mail"
                        )}`}
                        value={formData.invoiceEmail2}
                        onChange={handleChange("invoiceEmail2")}
                        helpText={t(
                          "(pildoma tik tuo atveju, jeigu yra poreikis/to be completed only if necessary)"
                        )}
                        error={validationErrors.invoiceEmail2}
                        labelHidden
                      />
                    </LegacyStack>
                  </BlockStack>
                  <Checkbox
                    label={
                      <>
                        <strong>Sutinku</strong>, kad sutartys, sąskaitos ir jų
                        priedai būtų siunčiami tik elektroniniu būdu nurodytais
                        elektroninio pašto adresais/ <strong>I agree</strong>{" "}
                        that all contracts, invoices, and related documents
                        shall be delivered only electronically to the designated
                        email addresses.
                      </>
                    }
                    checked={isAgreeDeliverElectronically}
                    onChange={setIsAgreeDeliverElectronically}
                  ></Checkbox>
                  <Checkbox
                    label={
                      <>
                        <strong>Nesutinku</strong>, kad dokumentai būtų
                        siunčiami tik elektroniniu būdu ir{" "}
                        <strong>sutinku</strong> sumokėti visas dokumentų
                        siuntimo ir parengimo siuntimui išlaidas/
                        <strong>I disagree</strong> with exclusive electronic
                        delivery of documents and <strong>agree</strong> to
                        cover all charges for document preparation and shipment.
                      </>
                    }
                    checked={isDisagreeDeliverElectronically}
                    onChange={setIsDisagreeDeliverElectronically}
                  />
                  {validationErrors.deliveryError && (
                    <p style={{ color: "red", marginTop: 4 }}>
                      Prašome pasirinkti tik vieną variantą / Please select only
                      one option.
                    </p>
                  )}
                </BlockStack>
              </Card>
              <Card roundedAbove="sm">
                <BlockStack gap="200">
                  <InlineGrid columns="1fr auto">
                    <div className="section_heading_">
                      <Text as="h2" variant="headingSm">
                        {`${t("customer_form-documents")}`}
                      </Text>
                    </div>
                    <Button
                      loading={isFileUploadLoading}
                      onClick={toggleOpenFileDialog}
                    >
                      {`${t("upload")}/Upload`}
                    </Button>
                    <div style={{ display: "none" }}>
                      <DropZone
                        allowMultiple={true}
                        openFileDialog={openFileDialog}
                        onDropAccepted={handleDropZoneDrop}
                        onFileDialogClose={toggleOpenFileDialog}
                      >
                        <DropZone.FileUpload />
                      </DropZone>
                    </div>
                  </InlineGrid>
                  
                    <Box
                      borderWidth="025"
                      borderColor="border"
                      padding="400"
                      background="bg-surface-secondary"
                      className="spec-section"
                    >
                      <BlockStack gap="200">
                        <Text as="p" fontWeight="bold">
                          Reikalingi pateikti dokumentai / Required documents to
                          be submitted:
                        </Text>
                        <BlockStack gap="100">
                          <Text as="p">
                            1. Įmonės registracijos dokumentų išrašo kopiją / A
                            copy of the Company's Certificate of Registration;
                          </Text>
                          <Text as="p">
                            2. Įmonės įgaliojimas, jeigu veikiama bus su
                            įgaliojimu / A Power of Attorney, if acting on
                            behalf of the Company under authorisation
                          </Text>
                        </BlockStack>
                      </BlockStack>
                    </Box>
                  <Text
                    as="p"
                    tone={validationErrors.documents ? "critical" : ""}
                  >
                    {formData.isFileUploadOptional
                      ? ""
                      : "Reikalingi pateikti dokumentai / Required documents to be submitted."}
                  </Text>
                    <ResourceList
                      resourceName={{
                        singular: t("customer_form-document"),
                        plural: t("customer_form-documents"),
                      }}
                      items={formData.uploadedFiles}
                      renderItem={(item) => {
                        const { id, filename } = item;

                        return (
                          <ResourceItem id={id}>
                            <InlineGrid columns="1fr auto">
                              <div className="min-w-0 break-words">
                                <Text variant="bodyMd" fontWeight="bold" as="h3">
                                  {filename}
                                </Text>
                              </div>
                              <Button
                                onClick={() => {
                                  if (
                                    window.confirm(
                                      t("customer_form-delete_confirmation")
                                    )
                                  ) {
                                    handleFileDelete(id);
                                  }
                                }}
                                destructive
                              >
                                {t("customer_form-delete")}
                              </Button>
                            </InlineGrid>
                          </ResourceItem>
                        );
                      }}
                    />
                  </BlockStack>
                </Card>
              <Card roundedAbove="sm" className="customer_form-signature_block">
                <BlockStack gap="200">
                  <div className="section_heading_">
                    <InlineGrid columns="1fr auto">
                      <Text as="h2" variant="headingSm">
                        5.{t("Patvirtinimai/ Declarations")}
                      </Text>
                    </InlineGrid>
                  </div>
                  <BlockStack gap="200">
                    {/* Row 1 */}
                    <div className="spec-section" style={{ padding: '12px 16px', borderRadius: 4 }}>
                    <div>
                      <strong>Patvirtinu,</strong> kad Bendrovei ir tiesiogiai
                      ar netiesiogiai jį/ją valdančiam (-tiems)
                      (kontroliuojančiam (-tiems)) asmenims ir/ar jų vykdomos
                      veiklos/tiekiamų produktų atžvilgiu, nėra taikomos
                      tarptautinės (tame tarpe ir Jungtinių Amerikos Valstijų)
                      sankcijos ar kitos Europos Sąjungos (toliau – ES)
                      nustatytos ribojamosios priemonės/{" "}
                      <strong>I hereby declare</strong> that neither the
                      Company, nor any persons directly or indirectly
                      controlling or exercising control over the Company and/or
                      their respective activities or supplied products, are
                      subject to any international sanctions (including those
                      imposed by the United States) or other restrictive
                      measures imposed by the European Union (hereinafter
                      referred to as the “EU”).
                    </div>

                    {/* Row 2 */}
                    <div>
                      <strong>Man žinoma,</strong>
                      kad jeigu ši deklaracija yra neteisinga, melaginga ir (ar)
                      klaidinanti, su Bendrove sudaryta Sutartis gali būti
                      nutraukta ir Bendrovė privalės atlyginti dėl šios
                      melagingos deklaracijos pateikimo patirtus nukentėjusios
                      šalies nuostolius/ I acknowledge that if this declaration
                      is found to be false, misleading, or inaccurate, the
                      Contract concluded with the Company may be terminated, and
                      the Company shall be liable to indemnify the injured Party
                      for any losses or damages incurred as a result of such
                      false declaration.
                    </div>
                    {/* Row 3 */}
                    <div>
                      <strong>Paaiškėjus</strong>
                      apie Bendrovei tiesiogiai ar netiesiogiai jį valdančiam
                      (-tiems) (kontroliuojančiam (-tiems)) asmeniui (-ims),
                      ir/ar jų vykdomos veiklos/tiekiamų produktų atžvilgiu,
                      taikomas tarptautines (tame tarpe ir Jungtinių Amerikos
                      Valstijų) sankcijas ar kitas ES nustatytas ribojamąsias
                      priemones, įsipareigoju nedelsiant apie tai Jus
                      informuoti/ Should it become known that the Company, any
                      persons directly or indirectly controlling it (or
                      exercising control over it) and/or their respective
                      activities or supplied products, are subject to
                      international sanctions (including those imposed by the
                      United States) or other restrictive measures imposed by
                      the European Union, I undertake to immediately notify you
                      thereof.
                    </div>
                    </div>
                  </BlockStack>

                  <Bleed marginInline="400">
                    <Box
                      background="bg-surface-secondary"
                      paddingBlock="300"
                      paddingInline="400"
                      // className="atstovas-block"
                    >
                      <BlockStack gap="400">
                        <LegacyStack>
                          <LegacyStack.Item fill>
                            <label htmlFor="customer-form-signerFullName">
                              {t("Atstovas/ Representative")}:
                            </label>
                          </LegacyStack.Item>
                          <div className="representative-fields">
                            <TextField
                              id="customer-form-signerFullName"
                              label={`${t("customer_form-signer-signerFullName")}`}
                              value={formData.signerFullName}
                              onChange={handleChange("signerFullName")}
                              autoComplete="off"
                              requiredIndicator
                              error={validationErrors.signerFullName}
                              helpText={`${t(
                                "(Vardas, pavardė/ First and last name)"
                              )}`}
                              labelHidden
                            />
                            <TextField
                              label={`${t("customer_form-signer-position")}`}
                              value={formData.signerPosition}
                              onChange={handleChange("signerPosition")}
                              autoComplete="off"
                              requiredIndicator
                              error={validationErrors.signerPosition}
                              helpText={`${t("(Pareigos / Position)")}`}
                              labelHidden
                            />
                          </div>
                        </LegacyStack>

                        <BlockStack gap="200">
                          <Text
                            as="p"
                            variant="bodySm"
                            tone="subdued"
                            className="confirm-disclaimer"
                          >
                            {t("customer_form-confirm_disclaimer")}
                          </Text>
                          <div className="confirm-button-wrap">
                            <Button
                              variant="primary"
                              submit
                              loading={formSubmitLoading}
                            >
                              {`${t("customer_form-confirm")}`}
                            </Button>
                          </div>
                        </BlockStack>
                      </BlockStack>
                      {showFormError && (
                        <InlineStack align="end" gap="200">
                          <Text tone="critical">
                            {`${t("customer_form-submit_error")}`}
                          </Text>
                        </InlineStack>
                      )}
                    </Box>
                  </Bleed>
                </BlockStack>
              </Card>
            </FormLayout>
          </Form>
        </div>
      </Page>
    </>
  );
}

export default CustomerForm;
